const OPENAI_CALLS_URL = "https://api.openai.com/v1/realtime/calls";

export function buildWebRtcSessionConfig(value = {}) {
  const config = {
    type: value.type || "realtime",
    model: value.model || "gpt-realtime-mini",
    output_modalities: value.output_modalities || ["audio"],
    ...(value.instructions ? { instructions: value.instructions } : {}),
    ...(value.tools ? { tools: value.tools } : {}),
    ...(value.tool_choice ? { tool_choice: value.tool_choice } : {}),
    ...(value.max_output_tokens != null ? { max_output_tokens: value.max_output_tokens } : {}),
  };
  const input = { ...(value.audio?.input || {}) };
  const output = { ...(value.audio?.output || {}) };
  delete input.format;
  delete output.format;
  input.turn_detection = {
    type: "server_vad",
    create_response: false,
    interrupt_response: false,
    prefix_padding_ms: 300,
    silence_duration_ms: 2000,
    threshold: 0.75,
  };
  config.audio = { input, output: { voice: "marin", ...output } };
  return config;
}

export class BrowserRealtimeVoice {
  constructor({ onEvent, onDisconnect, onPlaybackError }) {
    this.onEvent = onEvent;
    this.onDisconnect = onDisconnect;
    this.onPlaybackError = onPlaybackError;
    this.closed = false;
    this.ready = false;
  }

  async connect(session) {
    const secret = session?.client_secret?.value;
    const expiresAt = Number(session?.client_secret?.expires_at || 0);
    if (!secret || (expiresAt > 0 && expiresAt * 1000 <= Date.now())) {
      throw new Error("The voice connection credential expired. Start a new chat and try again.");
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof RTCPeerConnection === "undefined") {
      throw new Error("Voice chat requires a browser with microphone and WebRTC support.");
    }
    this.abortController = new AbortController();
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: {
        echoCancellation: true, noiseSuppression: true, autoGainControl: false,
      } });
      if (this.closed) {
        this.stream.getTracks().forEach((track) => track.stop());
        return;
      }
      this.track = this.stream.getAudioTracks()[0];
      if (!this.track) throw new Error("No microphone audio track is available.");
      this.track.enabled = false;
      this.peer = new RTCPeerConnection();
      this.audio = document.createElement("audio");
      this.audio.autoplay = true;
      this.peer.ontrack = (event) => {
        this.audio.srcObject = event.streams[0];
        this.audio.play().catch(() => this.onPlaybackError?.("Allow audio playback to hear the assistant."));
      };
      this.peer.onconnectionstatechange = () => {
        if (!this.closed && ["failed", "disconnected", "closed"].includes(this.peer.connectionState)) {
          this.onDisconnect?.(this.peer.connectionState);
        }
      };
      this.peer.addTrack(this.track, this.stream);
      this.channel = this.peer.createDataChannel("oai-events");
      const sessionUpdated = new Promise((resolve) => { this.resolveSessionUpdated = resolve; });
      this.channel.onmessage = (message) => {
        try {
          const event = JSON.parse(message.data);
          if (event.type === "session.updated") this.resolveSessionUpdated?.();
          this.onEvent?.(event);
        } catch { /* Ignore malformed provider events. */ }
      };
      const channelReady = new Promise((resolve, reject) => {
        this.channel.onopen = resolve;
        this.channel.onerror = () => reject(new Error("Voice event channel failed."));
      });
      const offer = await this.peer.createOffer();
      await this.peer.setLocalDescription(offer);
      const answer = await fetch(OPENAI_CALLS_URL, {
        method: "POST",
        headers: { Authorization: `Bearer ${secret}`, "Content-Type": "application/sdp" },
        body: offer.sdp,
        signal: this.abortController.signal,
      });
      if (!answer.ok) throw new Error(`Voice connection failed (${answer.status}). Start a new chat and retry.`);
      await this.peer.setRemoteDescription({ type: "answer", sdp: await answer.text() });
      await Promise.race([
        channelReady,
        new Promise((_, reject) => setTimeout(() => reject(new Error("Voice connection timed out.")), 15000)),
      ]);
      if (this.closed) return;
      this.ready = true;
      this.send({ type: "session.update", session: buildWebRtcSessionConfig(session.session_config) });
      await Promise.race([
        sessionUpdated,
        new Promise((_, reject) => setTimeout(() => reject(new Error("Voice session settings were not confirmed.")), 10000)),
      ]);
      if (this.closed) return;
      if (session.assistant_greeting) {
        this.send({ type: "response.create", response: {
          output_modalities: ["audio"],
          instructions: `Greet the user with exactly this message: ${session.assistant_greeting}`,
        } });
      } else {
        this.setMicrophoneEnabled(true);
      }
    } catch (error) {
      this.close();
      throw error;
    }
  }

  send(event) {
    if (!this.closed && this.channel?.readyState === "open") {
      this.channel.send(JSON.stringify(event));
      return true;
    }
    return false;
  }

  setMicrophoneEnabled(enabled) {
    if (this.track) this.track.enabled = Boolean(enabled);
  }

  interrupt() {
    this.send({ type: "response.cancel" });
    this.send({ type: "output_audio_buffer.clear" });
    this.audio?.pause();
    this.setMicrophoneEnabled(true);
  }

  resumePlayback() {
    this.audio?.play().catch(() => this.onPlaybackError?.("Allow audio playback to hear the assistant."));
  }

  close() {
    if (this.closed) return;
    this.closed = true;
    this.ready = false;
    this.abortController?.abort();
    this.stream?.getTracks().forEach((track) => track.stop());
    this.channel?.close();
    this.peer?.close();
    if (this.audio) {
      this.audio.pause();
      this.audio.srcObject = null;
    }
  }
}
